import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { 
  Users, 
  Clock, 
  Calendar, 
  DollarSign,
  TrendingUp,
  TrendingDown,
  Building2,
  UserMinus,
  AlertCircle,
  BarChart3
} from "lucide-react";
import { 
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, 
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, Legend,
  ComposedChart
} from "recharts";
import api from "../lib/api";
import { formatCurrency } from "../lib/currency";

const COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#6366f1', '#14b8a6', '#f97316'];

const Analytics = () => {
  const { currentBusiness } = useAuth();
  const [activeTab, setActiveTab] = useState("employees");
  const [employeeData, setEmployeeData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [leaveData, setLeaveData] = useState(null);
  const [payrollData, setPayrollData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!currentBusiness?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [empRes, attRes, leaveRes, payRes] = await Promise.all([
          api.get(`/analytics/employee?business_id=${currentBusiness.id}`),
          api.get(`/analytics/attendance?business_id=${currentBusiness.id}`),
          api.get(`/analytics/leaves?business_id=${currentBusiness.id}`),
          api.get(`/analytics/payroll?business_id=${currentBusiness.id}`)
        ]);
        setEmployeeData(empRes.data);
        setAttendanceData(attRes.data);
        setLeaveData(leaveRes.data);
        setPayrollData(payRes.data);
      } catch (error) {
        console.error("Failed to fetch analytics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [currentBusiness?.id]);

  if (!currentBusiness) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-slate-500">Please select a business to view analytics</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Analytics Dashboard</h1>
          <p className="text-slate-500">Comprehensive insights across your HR operations</p>
        </div>
        <Badge variant="outline" className="text-primary border-primary">
          <BarChart3 className="h-4 w-4 mr-1" />
          Real-time Data
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="employees" className="flex items-center gap-2" data-testid="tab-employees">
            <Users className="h-4 w-4" />
            Employees
          </TabsTrigger>
          <TabsTrigger value="attendance" className="flex items-center gap-2" data-testid="tab-attendance">
            <Clock className="h-4 w-4" />
            Attendance
          </TabsTrigger>
          <TabsTrigger value="leaves" className="flex items-center gap-2" data-testid="tab-leaves">
            <Calendar className="h-4 w-4" />
            Leaves
          </TabsTrigger>
          <TabsTrigger value="payroll" className="flex items-center gap-2" data-testid="tab-payroll">
            <DollarSign className="h-4 w-4" />
            Payroll
          </TabsTrigger>
        </TabsList>

        {/* EMPLOYEES TAB */}
        <TabsContent value="employees" className="space-y-6">
          {employeeData && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="glass">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Active Employees</p>
                        <p className="text-2xl font-bold text-slate-800">{employeeData.total_active}</p>
                      </div>
                      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-6 w-6 text-primary" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Inactive/Left</p>
                        <p className="text-2xl font-bold text-slate-800">{employeeData.total_inactive}</p>
                      </div>
                      <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
                        <UserMinus className="h-6 w-6 text-red-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Attrition Rate</p>
                        <p className="text-2xl font-bold text-slate-800">{employeeData.attrition_rate}%</p>
                      </div>
                      <div className={`h-12 w-12 rounded-full flex items-center justify-center ${employeeData.attrition_rate > 15 ? 'bg-red-100' : 'bg-green-100'}`}>
                        {employeeData.attrition_rate > 15 ? 
                          <TrendingUp className="h-6 w-6 text-red-600" /> :
                          <TrendingDown className="h-6 w-6 text-green-600" />
                        }
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Departments</p>
                        <p className="text-2xl font-bold text-slate-800">{employeeData.department_distribution.length}</p>
                      </div>
                      <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-purple-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Headcount Trend */}
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Headcount Trend (12 Months)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={employeeData.headcount_trend}>
                        <defs>
                          <linearGradient id="headcountGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Area type="monotone" dataKey="count" stroke="#8b5cf6" fill="url(#headcountGradient)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Department Distribution */}
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Department Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
                        <Pie
                          data={employeeData.department_distribution}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="45%"
                          outerRadius={80}
                          label={({ name, value, cx, x }) => {
                            // Truncate long names for better display
                            const displayName = name.length > 12 ? name.substring(0, 10) + '...' : name;
                            return `${displayName}: ${value}`;
                          }}
                          labelLine={{ strokeWidth: 1 }}
                        >
                          {employeeData.department_distribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [value, name]} />
                        <Legend 
                          layout="horizontal" 
                          verticalAlign="bottom" 
                          align="center"
                          wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Tenure Distribution */}
                <Card className="glass lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Tenure Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={employeeData.tenure_distribution}>
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* ATTENDANCE TAB */}
        <TabsContent value="attendance" className="space-y-6">
          {attendanceData && (
            <>
              {/* Summary Card */}
              <Card className="glass">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                      <Clock className="h-8 w-8 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Average Attendance Rate (30 days)</p>
                      <p className="text-3xl font-bold text-slate-800">{attendanceData.avg_attendance_rate}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Attendance Trend */}
                <Card className="glass lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Daily Attendance Rate (30 Days)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={attendanceData.attendance_trend}>
                        <defs>
                          <linearGradient id="attendanceGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
                        <YAxis tick={{ fontSize: 12 }} domain={[0, 100]} />
                        <Tooltip formatter={(value) => [`${value}%`, 'Attendance Rate']} />
                        <Area type="monotone" dataKey="rate" stroke="#10b981" fill="url(#attendanceGradient)" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Late Arrivals */}
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-500" />
                      Late Arrivals Trend
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={attendanceData.late_arrivals.slice(-14)}>
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="late" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Department Comparison */}
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Department-wise Attendance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={attendanceData.department_comparison} layout="vertical">
                        <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} />
                        <YAxis type="category" dataKey="department" tick={{ fontSize: 11 }} width={100} />
                        <Tooltip formatter={(value) => [`${value}%`, 'Rate']} />
                        <Bar dataKey="rate" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* LEAVES TAB */}
        <TabsContent value="leaves" className="space-y-6">
          {leaveData && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="glass">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Total Leaves (YTD)</p>
                        <p className="text-2xl font-bold text-slate-800">{leaveData.total_leaves_ytd}</p>
                      </div>
                      <Calendar className="h-8 w-8 text-primary" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Approved</p>
                        <p className="text-2xl font-bold text-green-600">{leaveData.approved_ytd}</p>
                      </div>
                      <Badge className="bg-green-100 text-green-700">Approved</Badge>
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Pending</p>
                        <p className="text-2xl font-bold text-amber-600">{leaveData.pending_ytd}</p>
                      </div>
                      <Badge className="bg-amber-100 text-amber-700">Pending</Badge>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Leave by Type */}
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Leave Distribution by Type</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
                        <Pie
                          data={leaveData.leave_by_type}
                          dataKey="count"
                          nameKey="type"
                          cx="50%"
                          cy="45%"
                          outerRadius={80}
                          label={({ type, count }) => {
                            const displayType = type.length > 12 ? type.substring(0, 10) + '...' : type;
                            return `${displayType}: ${count}`;
                          }}
                          labelLine={{ strokeWidth: 1 }}
                        >
                          {leaveData.leave_by_type.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [value, name]} />
                        <Legend 
                          layout="horizontal" 
                          verticalAlign="bottom" 
                          align="center"
                          wrapperStyle={{ fontSize: '12px', paddingTop: '10px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Monthly Trends */}
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Monthly Leave Trends</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={leaveData.monthly_trends}>
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="approved" stackId="a" fill="#10b981" name="Approved" />
                        <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pending" />
                        <Bar dataKey="rejected" stackId="a" fill="#ef4444" name="Rejected" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Department Patterns */}
                <Card className="glass lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Leaves by Department</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={leaveData.department_patterns}>
                        <XAxis dataKey="department" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Bar dataKey="leaves" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* PAYROLL TAB */}
        <TabsContent value="payroll" className="space-y-6">
          {payrollData && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="glass">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Total Payroll (YTD)</p>
                        <p className="text-2xl font-bold text-slate-800">{formatCurrency(payrollData.total_payroll_ytd)}</p>
                      </div>
                      <DollarSign className="h-8 w-8 text-green-600" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Average Salary</p>
                        <p className="text-2xl font-bold text-slate-800">{formatCurrency(payrollData.avg_salary)}</p>
                      </div>
                      <TrendingUp className="h-8 w-8 text-primary" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="glass">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Employees with Salary</p>
                        <p className="text-2xl font-bold text-slate-800">{payrollData.total_employees_with_salary}</p>
                      </div>
                      <Users className="h-8 w-8 text-primary" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Monthly Costs */}
                <Card className="glass lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Monthly Payroll Costs</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <ComposedChart data={payrollData.monthly_costs}>
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Legend />
                        <Bar dataKey="gross" fill="#8b5cf6" name="Gross Salary" radius={[4, 4, 0, 0]} />
                        <Line type="monotone" dataKey="net" stroke="#10b981" strokeWidth={2} name="Net Salary" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Department Distribution */}
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Salary by Department</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={payrollData.department_distribution} layout="vertical">
                        <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} />
                        <YAxis type="category" dataKey="department" tick={{ fontSize: 11 }} width={100} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="total_salary" fill="#8b5cf6" name="Total" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Salary Trends */}
                <Card className="glass">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold">Average Salary Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={payrollData.salary_trends}>
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Line type="monotone" dataKey="avg_salary" stroke="#8b5cf6" strokeWidth={2} dot={{ fill: '#8b5cf6' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Analytics;
